/**
 * Table alignment — one rule for every table in the application.
 *
 *   num  numbers, amounts, quantities, numbers with a unit or a %  → right
 *   txt  names, descriptions, parameters, sources, messages, dates → left
 *   ctr  short codes, status badges, flags, ticks, action buttons  → centre
 *
 * A column's heading and its values always get the SAME class, so the two can
 * never drift apart. Before this, alignment was set per cell — a left-aligned
 * heading object in one file, a centring rule in the stylesheet, an inline
 * override in a third place — and the heading routinely sat somewhere other
 * than the figures under it.
 *
 * Two ways a column gets its class, in order:
 *
 *   1. Explicit. A <th> or <td> carrying className num / txt / ctr (usually
 *      from `ac(header)` below) states the column's kind. The heading's class
 *      is copied down to every value in that column that does not state its
 *      own, so a table only has to classify its headings.
 *   2. Inferred. Everything else is read from the values actually on screen —
 *      numbers right, codes and ticks centred, words left — and the same answer
 *      goes on the heading and every value. The first column is the row's
 *      name and reads from the left unless it holds figures.
 *
 * Inference writes a `data-al` attribute, never a class, so it cannot be
 * mistaken for — or override — an explicit class. index.css turns both into
 * text-align. A table can opt out with data-align="off".
 */

import { useEffect } from "react";

/* ── 1. From the heading ──────────────────────────────────────────────────── */

const NUMERIC_COL = /(₹|%|m²|m³|\(m\)|°c|\bqty\b|quantity|\brate\b|rates?\s*\(|amount|price|cost|stock|\bach\b|\bpa\b|\bocc\b|\bcov\b|coverage|velocity|share|count|total|gross|\bnet\b|wastage|perimeter|openings|orders|items|segments|cfm|\bkw\b|\btr\b|length|width|height|area|volume|files|rooms|no\.$)/i;
const TEXT_COL = /(name|description|nomenclature|customer|vendor|site|notes?|remarks?|element|item|drawing|project|inquiry|engineer|salesperson|change|action|by$|path|parameter|source|message|reason|title|category|subcategory|date|when|deleted|created|updated|\bat$)/i;
const CODE_COL = /^(status|state|kind|class|unit|uom|zone|type|stage|gate|severity|flag|grade|level|engine|rev|revision|ok|mode|hsn|sac|hsn \/ sac|hsn\/sac|sku|zoho sku|gst %?)$/i;

/** The class a column should carry, from what its heading says it holds.
 *  Returns "" when the heading does not say — the column is then aligned from
 *  its values, which is better than guessing from a word. */
export function ac(header) {
  const h = String(header || "").trim();
  if (!h) return "ctr";                       // an actions / tick column
  if (NUMERIC_COL.test(h)) return "num";
  if (TEXT_COL.test(h)) return "txt";
  if (CODE_COL.test(h)) return "ctr";
  return "";
}

/* ── 2. From the values ───────────────────────────────────────────────────── */

const KINDS = ["num", "txt", "ctr"];
const EMPTY = /^(?:—|–|-|−|·|…|\.\.\.|n\/?a|nil|none|null|not set|not provided)$/i;
// A number, a range, ± tolerance or a short "S / L / T" set of figures, with an
// optional currency in front and a
// short unit or % behind it. Dates ("19 Oct 2026", "2026-10-19") and times do
// not match: their tail holds digits or a colon.
const NUMBER = /^(?:[₹$€£]\s*)?[-+−±≈~≤≥<>]?\s*\d[\d,]*(?:\.\d+)?(?:\s*(?:[-–—…]|to|±|\/|×|x)\s*(?:[₹$€£]\s*)?[-+−]?\d[\d,]*(?:\.\d+)?){0,3}\s*(?:%|‰|[A-Za-zµ°²³·/()\-.\s]{1,16})?$/;
// Dates and times read as text (left), whatever their digits suggest.
const DATE = /^(?:\d{1,4}[-/.](?:\d{1,2}|[A-Za-z]{3})[-/.]\d{2,4}\b|\d{1,2}\s+[A-Za-z]{3,9}\s+\d{2,4}\b|\d{1,2}:\d{2}\b)/;
const SYMBOL = /^[^\p{L}\p{N}]{1,3}$/u;
const YESNO = /^(?:yes|no|y|n|ok|on|off|true|false)$/i;

function explicitOf(cell) {
  const cl = cell.classList;
  for (const k of KINDS) if (cl.contains(k)) return k;
  if (cl.contains("cell-text")) return "txt";
  const a = cell.style && cell.style.textAlign;
  if (a === "right" || a === "end") return "num";
  if (a === "left" || a === "start") return "txt";
  if (a === "center") return "ctr";
  return "";
}

/** What one cell holds: "num" | "ctr" | "code" | "short" | "word" | "" (nothing).
 *  "short" is one or two plain words ("Approved", "Air lock") — a status when
 *  the heading says status, a name otherwise. */
export function kindOfCell(cell) {
  if (cell.querySelector("input[type=number]")) return "num";
  if (cell.querySelector("textarea")) return "word";
  // A text box reads as what is typed in it — many figure boxes here are text
  // inputs so a half-typed "1." survives; an empty one says nothing.
  const box = cell.querySelector("input:not([type]), input[type=text], input[type=search]");
  // A dropdown reads as the option it shows, not the list of every option.
  let raw = cell.textContent || "";
  cell.querySelectorAll("select").forEach(sel => {
    const o = sel.selectedOptions && sel.selectedOptions[0];
    raw = raw.replace(sel.textContent, ` ${o ? o.text : ""} `);
  });
  let text = raw.replace(/\s+/g, " ").trim();
  if (box) {
    const v = String(box.value || "").trim();
    if (!v) return /decimal|numeric/.test(box.inputMode || "") ? "num" : "";
    text = v;
  }
  if (!text) {
    // Only a control or a mark: a checkbox, a button, an icon.
    return cell.querySelector("input, button, select, svg, img, a") ? "ctr" : "";
  }
  if (EMPTY.test(text)) return "";
  if (DATE.test(text)) return "word";
  if (NUMBER.test(text) && !/\bago\b/i.test(text)) return "num";
  if (SYMBOL.test(text) || YESNO.test(text)) return "ctr";
  const kids = cell.querySelectorAll("button, a");
  if (kids.length && [...kids].map(b => b.textContent).join("").replace(/\s+/g, "").length
      >= text.replace(/\s+/g, "").length) return "ctr";          // only buttons / links
  if (!/[a-z]/.test(text) && text.length <= 24) return "code";   // BLOCK, AHU-01, ISO 7
  if (text.length <= 16 && text.split(" ").length <= 2) return "short";
  return "word";
}

// A heading that ends in a unit ("Face vel m/s", "SHGC 0–1") is a figure column
// even before any figure is entered.
const UNIT_HEAD = /(\s|\()(m|mm|m\/s|kg\/h|\/h|h|0[–-]1|w\/m²k?|pa|kw|cfm|tr|%|°c|m²|m³|g\/kg|kj\/kg|w|kwh|hp|nos?)\)?\s*\*?$/i;
// Identifiers that happen to be digits (an HSN code) are codes, not quantities.
const ID_HEAD = /^(hsn|sac|hsn\s*\/\s*sac|sku|zoho sku|code|.+\scode|pin|pin code|room no\.?|tag|id)$/i;

/** One column's class from its heading text and its cells. */
export function inferColumn(kinds, header, index) {
  const h = String(header || "").replace(/\s+/g, " ").trim();
  const seen = kinds.filter(Boolean);
  if (ID_HEAD.test(h)) return index === 0 ? "txt" : "ctr";
  if (!seen.length) {
    if (UNIT_HEAD.test(h)) return "num";
    return ac(h) || (index === 0 ? "txt" : "ctr");
  }
  const n = (k) => seen.filter(x => x === k).length;
  if (n("num") / seen.length >= 0.6) return "num";
  const words = n("word"), short = n("short");
  if (index === 0) return words || short || n("code") ? "txt" : "ctr";
  if (words) return "txt";
  if (!h) return "ctr";                        // an unlabelled column: actions, marks
  // One- or two-word values ("Approved", "kW", "Air lock") centre when the
  // heading says status / unit / zone, and read from the left otherwise.
  if (short) return CODE_COL.test(h) ? "ctr" : "txt";
  return "ctr";                                // codes, ticks, buttons
}

/** Place every cell of a table on a grid that honours colSpan / rowSpan. */
function layout(table) {
  const rows = Array.from(table.rows);
  const taken = [];
  const out = [];
  rows.forEach((tr, r) => {
    taken[r] = taken[r] || [];
    let c = 0;
    const head = tr.parentElement && tr.parentElement.tagName === "THEAD";
    for (const cell of tr.cells) {
      while (taken[r][c]) c++;
      const cs = Math.max(1, cell.colSpan || 1);
      const rs = Math.max(1, cell.rowSpan || 1);
      for (let i = 0; i < rs; i++) {
        taken[r + i] = taken[r + i] || [];
        for (let j = 0; j < cs; j++) taken[r + i][c + j] = true;
      }
      out.push({ cell, r, c, cs, head });
      c += cs;
    }
  });
  // No <thead>: a first row made only of <th> is the heading row.
  if (!out.some(p => p.head) && rows.length > 1) {
    const first = rows[0];
    if (first.cells.length && [...first.cells].every(x => x.tagName === "TH")) {
      out.forEach(p => { if (p.r === 0) p.head = true; });
    }
  }
  return out;
}

function setAl(cell, k) {
  if (k) { if (cell.getAttribute("data-al") !== k) cell.setAttribute("data-al", k); }
  else if (cell.hasAttribute("data-al")) cell.removeAttribute("data-al");
}

/** Align one table: the same class on a column's heading and its values. */
export function alignTable(table) {
  if (!table || table.getAttribute("data-align") === "off") return;
  const cells = layout(table);
  const cols = new Map();          // column index → {heads, body}
  for (const p of cells) {
    if (p.cs !== 1) continue;
    if (!cols.has(p.c)) cols.set(p.c, { heads: [], body: [] });
    cols.get(p.c)[p.head ? "heads" : "body"].push(p.cell);
  }
  const kindOf = new Map();
  for (const [c, { heads, body }] of cols) {
    const leaf = heads[heads.length - 1];
    let k = (leaf && explicitOf(leaf)) || "";
    if (!k) {
      const ex = body.map(explicitOf).find(Boolean);
      k = ex || inferColumn(body.map(kindOfCell), leaf ? leaf.textContent : "", c);
    }
    kindOf.set(c, k);
  }
  for (const p of cells) {
    if (explicitOf(p.cell)) { setAl(p.cell, ""); continue; }
    // A heading over several columns (a group) centres over them; a value
    // spanning columns (a note, a "no rows" line) keeps the default left.
    setAl(p.cell, p.cs === 1 ? kindOf.get(p.c) : (p.head ? "ctr" : ""));
  }
}

/* ── 3. Keeping it applied ────────────────────────────────────────────────── */

/** Mount once at the application root. Aligns every table now and again
 *  whenever a table's rows, cells or text change — coalesced to one pass per
 *  animation frame, which runs before the browser paints, so a column never
 *  shows a frame in the wrong place. */
export function useTableAligner() {
  useEffect(() => {
    if (typeof MutationObserver === "undefined") return undefined;
    const dirty = new Set();
    let frame = 0;
    const flush = () => {
      frame = 0;
      for (const t of dirty) if (t.isConnected) alignTable(t);
      dirty.clear();
    };
    const mark = (node) => {
      if (!node) return;
      const el = node.nodeType === 1 ? node : node.parentElement;
      if (!el) return;
      const t = el.closest("table");
      if (t) dirty.add(t);
      if (node.nodeType === 1 && node.querySelectorAll) {
        node.querySelectorAll("table").forEach(x => dirty.add(x));
      }
    };
    const obs = new MutationObserver((records) => {
      for (const rec of records) {
        mark(rec.target);
        rec.addedNodes && rec.addedNodes.forEach(mark);
      }
      if (dirty.size && !frame) frame = requestAnimationFrame(flush);
    });
    document.querySelectorAll("table").forEach(t => dirty.add(t));
    flush();
    obs.observe(document.body, { childList: true, subtree: true, characterData: true,
                                 attributes: true, attributeFilter: ["class", "colspan", "rowspan"] });
    return () => { obs.disconnect(); if (frame) cancelAnimationFrame(frame); };
  }, []);
}
